import type { ReactNode } from "react";

import type { IconName } from "../Icon/Icon";

// The Settings menu model — shared by mobile's ProfileScreen and the
// web-rnw console (one IA, two thin mounts). Each mount builds this from
// its own i18n strings and supplies the per-row action via onSelect(key);
// the shared SettingsMenuView is purely presentational so the navigation
// mechanism (native push vs state-based detail) stays a platform seam.
//
// A section may carry `custom` content (rendered after its rows) for the
// bits that aren't simple nav rows — the coach edges + version/health
// cards on mobile. Web omits those, and only lists rows whose detail is
// already lifted, so the menu naturally tracks lift coverage.

export type SettingsMenuRow = {
  /** Stable action key (the mount maps it to navigate / open-modal / select). */
  key: string;
  title: string;
  sub: string;
  /** Optional leading icon (the dense nav shows it too, as a finding aid). Lets the
   *  same row serve icon-forward action lists like the Create-hub tiles. */
  icon?: IconName;
  /** The language `sub` is written in when it is not the UI's (screen readers).
   *  Such a sub is a way back, so the dense nav shows it too. */
  subLanguage?: string;
};

export type SettingsMenuSection = {
  label: string;
  rows: SettingsMenuRow[];
  /** Non-row content appended after this section's rows (mobile specials). */
  custom?: ReactNode;
};

// #390: the "Language & region" section, first in Settings on mobile and web.
// Its title is translated; the English word "Language" sits under it so
// someone who set the wrong language can still find the way back. Skipped
// when the UI already is English (it would say the same thing twice).
// Both hosts build it from here, so the two menus cannot drift on it.
export const LANGUAGE_REGION_KEY = "languageRegion";
export function languageRegionSection(title: string, language: string): SettingsMenuSection {
  const isEnglish = language.toLowerCase().startsWith("en");
  return {
    label: title,
    rows: [
      {
        key: LANGUAGE_REGION_KEY,
        title,
        // English UI: the sub says what is inside; otherwise the bare English
        // "Language" is the way back.
        sub: isEnglish ? "Language, date format" : "Language",
        subLanguage: "en",
        icon: "globe",
      },
    ],
  };
}
